import {
  Button,
  Col,
  Flex,
  Modal,
  Row,
  Table,
  Tag,
  Tooltip,
  message,
  Result,
  Typography,
} from "antd";

import PageHeader from "../../components/PageHeader";
import { useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { useAPI } from "../../hooks/useAPI";
import { ENDPOINTS } from "../../constants/endpoints.constant";
import { getCall, postCall } from "../../constants/apicalls.constant";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import type { ColumnsType } from "antd/es/table";
import axios from "axios";
import downloadCSV from "../../utils/downloadCSV";
import dayjs from "dayjs";
import { formatDisplayDate } from "../../utils/DateAndTimeConvertor";
import PayoutRequestForm from "./workspace-payout-form";
import { fetchMyBankData } from "../../store/slices/banklist.slice";
import { useNavigate } from "react-router";
import PayoutCurrencySummaryCard from "../../components/payouts/PayoutCurrencySummaryCard";
import { formatPayoutAmount, PayoutCurrencyTotals } from "../../utils/payoutDisplay";
import PayoutStatusCell from "../../components/payouts/PayoutStatusCell";

const { Text } = Typography;

const requestPayoutButtonStyle: CSSProperties = {
  backgroundColor: "#000",
  borderColor: "#000",
  color: "#fff",
  fontWeight: 500,
};

type EventPayoutRow = {
  event_id: string;
  event_name: string;
  event_slug: string;
  event_status: string;
  is_group: boolean;
  start_date: string;
  payout: PayoutCurrencyTotals;
};

type WorkspacePayoutSummaryResponse = {
  status: boolean;
  data: {
    events: EventPayoutRow[];
    totals: PayoutCurrencyTotals;
  };
};

type PayoutRequestRow = {
  id: string;
  amount: string;
  created_at: string;
  status: string;
  currency?: string;
  admin_note?: string | null;
  payment_screenshot_attachment_id?: string | null;
  payment_screenshot_url?: string | null;
  event_id?: string;
  event?: { id: string; name: string };
  bank_account: {
    id: string;
    display_label: string;
    bank_name: string;
  };
};

type PayoutRequestsListResponse = {
  status: boolean;
  message: string;
  data: {
    current_page: number;
    last_page: number;
    total: number;
    per_page: number;
    data: PayoutRequestRow[];
  };
};

type EventAvailableRow = {
  key: string;
  event_id: string;
  event_name: string;
  event_status: string;
  available_payout: number;
};

const WorkspacePayouts = () => {
  const user = useSelector((state: RootState) => state.user);
  const { data: bankData } = useSelector((state: RootState) => state.banklist);
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();

  const [requestsPage, setRequestsPage] = useState(1);
  const [payoutModal, setPayoutModal] = useState<null | {
    open: boolean;
    event_id?: string;
    event_name?: string;
    available_payout?: number;
  }>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const workspaceId = user?.workspace?.id;
  const userRole = user?.workspace?.role || "owner";

  const { response, loading, error, trigger } =
    useAPI<WorkspacePayoutSummaryResponse>(
      ENDPOINTS.workspace_payout_summary,
      true,
      [],
      getCall({
        workspace_id: workspaceId,
        user_role: userRole,
        organizer: true,
      }),
    );

  const {
    response: requestsResponse,
    loading: requestsLoading,
    error: requestsError,
    trigger: fetchRequests,
  } = useAPI<PayoutRequestsListResponse>(ENDPOINTS.payout, true, [requestsPage], getCall({
    limit: 10,
    page: requestsPage,
    with: ["bankAccount", "event"],
    workspace_id: workspaceId,
    organizer: true,
  }));

  const { loading: payoutLoading, trigger: submitPayout } = useAPI<{
    status: boolean;
    message: string;
  }>(ENDPOINTS.payout);

  const eventNameById = useMemo(() => {
    const map: Record<string, string> = {};
    response?.data?.events?.forEach((ev) => {
      map[ev.event_id] = ev.event_name;
    });
    return map;
  }, [response?.data?.events]);

  const eventAvailableRows: EventAvailableRow[] = useMemo(() => {
    return (
      response?.data?.events
        ?.filter((event) => (event.payout?.available_payout ?? 0) > 0)
        .map((event) => ({
          key: event.event_id,
          event_id: event.event_id,
          event_name: event.event_name,
          event_status: event.event_status,
          available_payout: event.payout.available_payout,
        })) || []
    );
  }, [response?.data?.events]);

  useEffect(() => {
    if (payoutModal?.event_id) {
      dispatch(fetchMyBankData({ id: payoutModal.event_id }));
    }
  }, [payoutModal?.event_id, dispatch]);

  const handleExportCSV = async () => {
    try {
      setExportLoading(true);
      const url = `${
        import.meta.env.VITE_APP_API_URL
      }/${ENDPOINTS.workspace_payout_summary}?workspace_id=${workspaceId}&user_role=${userRole}&organizer=true&export=true`;
      const res = await axios.get(url, { withCredentials: true });
      downloadCSV(
        res.data,
        ["workspace_payouts", dayjs().format("YYYY_MM_DD__HH_mm_ss")].join(
          "__",
        ),
      );
    } catch {
      message.error("Failed to export CSV");
    } finally {
      setExportLoading(false);
    }
  };

  const handlePayoutSubmit = async (values: {
    amount: string;
    bank_account_id: string;
    organizer_note?: string;
  }) => {
    if (!payoutModal?.event_id) return;

    const amountNum = parseFloat(String(values.amount).replace(/,/g, ""));
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      message.error("Enter a valid amount");
      return;
    }
    if (amountNum > (payoutModal.available_payout ?? 0)) {
      message.error(
        `Amount cannot exceed available payout (${formatPayoutAmount("INR", payoutModal.available_payout ?? 0)})`,
      );
      return;
    }

    const res = await submitPayout(
      postCall({
        ...values,
        event_id: payoutModal.event_id,
        currency: "INR",
        workspace_id: workspaceId,
      }),
    );
    if (res?.status) {
      message.success("Payout request submitted successfully");
      setPayoutModal(null);
      setRequestsPage(1);
      trigger(
        getCall({
          workspace_id: workspaceId,
          user_role: userRole,
          organizer: true,
        }),
      );
      fetchRequests(
        getCall({
          limit: 10,
          page: 1,
          with: ["bankAccount", "event"],
          workspace_id: workspaceId,
          organizer: true,
        }),
      );
    } else {
      message.error(res?.message || "Failed to submit payout request");
    }
  };

  const openRequestForRow = (row: EventAvailableRow) => {
    setPayoutModal({
      open: true,
      event_id: row.event_id,
      event_name: row.event_name,
      available_payout: row.available_payout,
    });
  };

  const requestColumns: ColumnsType<PayoutRequestRow> = [
    {
      title: "Event",
      key: "event",
      width: 200,
      fixed: "left",
      render: (_, record) =>
        record.event?.name ||
        eventNameById[record.event_id ?? ""] ||
        "—",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 140,
      render: (val: string) =>
        formatPayoutAmount("INR", parseFloat(val) || 0),
    },
    {
      title: "Requested On",
      dataIndex: "created_at",
      key: "created_at",
      width: 140,
      render: (value: string) => formatDisplayDate(value),
    },
    {
      title: "Bank Account",
      key: "bank_account",
      width: 180,
      render: (_, record) => (
        <span>{record.bank_account?.display_label || "N/A"}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (status: string, record) => (
        <PayoutStatusCell
          status={status}
          record={record as Record<string, unknown>}
        />
      ),
    },
  ];

  const modalEventColumns: ColumnsType<EventAvailableRow> = [
    {
      title: "Event",
      dataIndex: "event_name",
      key: "event_name",
    },
    {
      title: "Status",
      dataIndex: "event_status",
      key: "event_status",
      width: 120,
      render: (status: string) => {
        const isCompleted = status === "completed";
        return (
          <Tag color={isCompleted ? "green" : "default"}>
            {status
              ? status.charAt(0).toUpperCase() + status.slice(1)
              : "—"}
          </Tag>
        );
      },
    },
    {
      title: "Available Payout",
      dataIndex: "available_payout",
      key: "available_payout",
      render: (val: number) => (
        <Text strong style={{ color: "#52c41a" }}>
          {formatPayoutAmount("INR", val)}
        </Text>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 150,
      render: (_, record) => {
        const isCompleted = record.event_status === "completed";
        return (
          <Tooltip
            title={
              !isCompleted
                ? "Payout requests are only available after the event is completed"
                : undefined
            }
          >
            <Button
              type="primary"
              size="small"
              style={isCompleted ? requestPayoutButtonStyle : undefined}
              disabled={!isCompleted}
              onClick={() => isCompleted && openRequestForRow(record)}
            >
              Request Payout
            </Button>
          </Tooltip>
        );
      },
    },
  ];

  if (error) {
    return (
      <div className="p-24">
        <Result
          status="error"
          title="Something went wrong"
          subTitle="Unable to load payout report."
        />
      </div>
    );
  }

  return (
    <>
      <div className="p-24 w-full max-w-full">
        <PageHeader
          title="Payout Report"
          backBtn={true}
          description="View payout requests and workspace earnings summary."
          additionalButton={[
            {
              name: "Export CSV",
              route: "",
              fn: handleExportCSV,
            },
            {
              name: "Request Payout",
              route: "",
              fn: () => setPayoutModal({ open: true }),
              type: "primary",
              style: requestPayoutButtonStyle,
            },
          ]}
        />

        {response?.data?.totals && (
          <Row className="mb-[24px] w-full">
            <Col xs={24}>
              <PayoutCurrencySummaryCard
                totals={{
                  ...response.data.totals,
                  pending_payout: response.data.totals.pending_payout ?? 0,
                }}
                summaryLabel="Payout Summary"
                showChargeDetails={false}
              />
            </Col>
          </Row>
        )}

        <Text strong className="block mb-[12px] text-[16px]">
          Payout Requests
        </Text>

        <Table
          bordered
          rowKey="id"
          className="w-full"
          scroll={{ x: "max-content" }}
          columns={requestColumns}
          loading={loading || requestsLoading || exportLoading}
          dataSource={requestsResponse?.data?.data ?? []}
          pagination={{
            current: requestsResponse?.data?.current_page ?? requestsPage,
            pageSize: requestsResponse?.data?.per_page ?? 10,
            total: requestsResponse?.data?.total ?? 0,
            onChange: (page) => setRequestsPage(page),
            showSizeChanger: false,
          }}
          locale={{
            emptyText:
              requestsLoading || loading
                ? "Loading..."
                : requestsError
                  ? "Unable to load payout requests"
                  : "No payout requests yet",
          }}
        />
      </div>

      <Modal
        open={!!payoutModal?.open}
        closable
        onCancel={() => setPayoutModal(null)}
        footer={null}
        width={payoutModal?.event_id ? 520 : 720}
        title={
          payoutModal?.event_name
            ? `Request Payout — ${payoutModal.event_name}`
            : "Request Payout"
        }
        destroyOnClose
        styles={{ body: { padding: "16px 24px 24px" } }}
      >
        {!payoutModal?.event_id ? (
          <>
            <div
              style={{
                background: "#f5f5f5",
                borderRadius: 8,
                padding: "10px 14px",
                marginBottom: 16,
                fontSize: 13,
                color: "#595959",
              }}
            >
              Select an event with available balance to request a payout.
            </div>
            <Text strong className="block mb-[8px] text-[13px]">
              Events with Available Balance
            </Text>
            <Table
              size="small"
              bordered
              rowKey="key"
              columns={modalEventColumns}
              dataSource={eventAvailableRows}
              pagination={eventAvailableRows.length > 5 ? { pageSize: 5 } : false}
              locale={{
                emptyText: loading
                  ? "Loading..."
                  : "No events with available payout balance",
              }}
            />
          </>
        ) : payoutModal.available_payout !== undefined &&
          payoutModal.available_payout <= 0 ? (
          <>
            <Text type="secondary">
              No available payout balance for this event.
            </Text>
            <Flex justify="space-between" align="center" className="mt-[16px]">
              <Button
                onClick={() =>
                  setPayoutModal({
                    open: true,
                    event_id: undefined,
                    event_name: undefined,
                    available_payout: undefined,
                  })
                }
              >
                Back
              </Button>
            </Flex>
          </>
        ) : bankData?.data?.data && bankData.data.data.length > 0 ? (
          <PayoutRequestForm
            maxAmount={payoutModal.available_payout ?? 0}
            loading={payoutLoading}
            onSubmit={handlePayoutSubmit}
            onBack={() =>
              setPayoutModal({
                open: true,
                event_id: undefined,
                event_name: undefined,
                available_payout: undefined,
              })
            }
          />
        ) : (
          <>
            <h5 className="text-black text-sm">
              No bank details found. Please add bank details first to request a
              payout.
            </h5>
            <Flex justify="space-between" align="center" className="mt-[16px]">
              <Button
                onClick={() =>
                  setPayoutModal({
                    open: true,
                    event_id: undefined,
                    event_name: undefined,
                  })
                }
              >
                Back
              </Button>
              <Button
                type="primary"
                onClick={() => navigate("/workspace?tab=kyc")}
              >
                Add Bank Account
              </Button>
            </Flex>
          </>
        )}
      </Modal>
    </>
  );
};

export default WorkspacePayouts;
